import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import { createStandaloneOrder, fetchOrderList } from './api'
import type { OrderListFilters } from './schema'
import type { CreateGroupOrderInput } from '@/features/group/order-charge'
import { orderListKeys } from './keys'

export { orderListKeys } from './keys'

/** Omit paging from the cache key so page changes reuse the same filter bucket. */
type OrderListFilterKey = Omit<OrderListFilters, 'page'>

export const DEFAULT_ORDER_LIST_PAGE_SIZE = 5

export function orderListQueryOptions(filters: OrderListFilters = {}) {
  return queryOptions({
    queryKey: orderListKeys.list(filters),
    queryFn: ({ signal }) => fetchOrderList(filters, signal),
    placeholderData: keepPreviousData,
  })
}

export function orderListInfiniteQueryOptions(
  filters: OrderListFilterKey = {},
  perPage = DEFAULT_ORDER_LIST_PAGE_SIZE,
) {
  return infiniteQueryOptions({
    queryKey: orderListKeys.infinite({ ...filters, perPage }),
    // Reuse the route loader's response on mount instead of immediately issuing
    // another request. Explicit mutation invalidation still refreshes the list.
    staleTime: 30_000,
    queryFn: ({ pageParam, signal }) =>
      fetchOrderList({ ...filters, perPage, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasNext ? lastPage.pagination.page + 1 : undefined,
  })
}

/** Create a manual order with no group (Order Mandiri), refreshing the flat list. */
export function useCreateStandaloneOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateGroupOrderInput) => createStandaloneOrder(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orderListKeys.all })
    },
  })
}
